// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Dialog component for transferring inventory between locations.
 * Handles moving items from one location to another with quantity tracking.
 */
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { useToast } from '@/contexts/toast-context';
import { useInventoryCheck } from '@/inventory/hooks';
import { invalidateInventory } from '@/inventory/utilities/invalidate-inventory';
import { moveInventory } from '@/items/api/move-inventory';
import { FormBulkItemSearch } from '@/items/components/form-bulk-item-search';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const transferInventorySchema = z.object({
  item: z.object(
    {
      id: z.string(),
      key: z.string(),
      name: z.string(),
    },
    { required_error: 'Item is required' }
  ),
  fromLocation: z.object(
    {
      id: z.string(),
      key: z.string(),
      name: z.string(),
    },
    { required_error: 'From location is required' }
  ),
  toLocation: z.object(
    {
      id: z.string(),
      key: z.string(),
      name: z.string(),
    },
    { required_error: 'To location is required' }
  ),
  quantity: z
    .string({ required_error: 'Quantity is required' })
    .min(1, 'Quantity must be greater than 0'),
  note: z.string().optional(),
});

type TransferInventoryFormData = z.infer<typeof transferInventorySchema>;

interface Props {
  isOpen: boolean;
  item?: SparseItem;
  location?: SparseLocation;
  onClose: () => void;
}

export function TransferInventoryDialog(props: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );

  const { control, handleSubmit, reset, setError } =
    useForm<TransferInventoryFormData>({
      resolver: zodResolver(transferInventorySchema),
      defaultValues: {
        item: props.item,
        fromLocation: props.location,
        quantity: '',
        note: '',
      },
    });

  const watchedValues = useWatch({
    control,
    name: ['fromLocation', 'item', 'toLocation'],
  });

  const selectedFromLocation = watchedValues[0] as SparseLocation | undefined;
  const selectedItem = watchedValues[1] as SparseItem | undefined;
  const selectedToLocation = watchedValues[2] as SparseLocation | undefined;

  const { inventoryMap, isLoading, isFetching } = useInventoryCheck(
    selectedItem?.key || '',
    { includeNonInventory: true }
  );

  // Compute available quantities for display (without mutating form data)
  const fromLocationQuantity = useMemo(() => {
    if (!selectedItem || !selectedFromLocation) return null;
    if (isLoading || isFetching) return 'loading';
    return inventoryMap.get(selectedFromLocation.id)?.get(selectedItem.id) ?? 0;
  }, [inventoryMap, selectedItem, selectedFromLocation, isLoading, isFetching]);

  const toLocationQuantity = useMemo(() => {
    if (!selectedItem || !selectedToLocation) return null;
    if (isLoading || isFetching) return 'loading';
    return inventoryMap.get(selectedToLocation.id)?.get(selectedItem.id) ?? 0;
  }, [inventoryMap, selectedItem, selectedToLocation, isLoading, isFetching]);

  const locationInventoryDecorator = selectedItem
    ? isLoading || isFetching
      ? (loc: SparseLocation) => {
          loc.name += ' (loading...)';
          return loc;
        }
      : (loc: SparseLocation) => {
          const qty = inventoryMap.get(loc.id)?.get(selectedItem.id) ?? 0;
          loc.name += ` (${qty} available)`;
          return loc;
        }
    : (loc: SparseLocation) => loc;

  useEffect(() => {
    if (props.isOpen) {
      reset({
        item: props.item,
        fromLocation: props.location,
        quantity: '',
        note: '',
      });
    }
  }, [props.isOpen, props.location, props.item, reset]);

  const transferInventoryMutation = useMutation({
    mutationFn: moveInventory,
    onSuccess: () => {
      toast.showToast({
        element: <Typography>Inventory transferred successfully</Typography>,
        severity: 'success',
        duration: 3000,
      });
      invalidateInventory(queryClient);
      handleClose();
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (
        apiError.apiStatusCode === ERROR_CODE_MAPPING.part_invalid_quantity ||
        apiError.apiStatusCode ===
          ERROR_CODE_MAPPING.inventory_removal_exceeds_stock
      ) {
        setErrorMessage('There is insufficient quantity at this location');
      } else {
        setErrorMessage(
          'An error occurred attempting to adjust your inventory'
        );
      }
    },
  });

  const handleClose = () => {
    setErrorMessage(undefined);
    props.onClose();
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);
    window.open(url, '_blank');
  };

  const onSubmit = (data: TransferInventoryFormData) => {
    const locationInventory =
      inventoryMap.get(data.fromLocation.id)?.get(data.item.id) ?? 0;
    const quantity = Number(data.quantity);
    if (quantity > locationInventory) {
      setError('quantity', {
        type: 'validate',
        message: `You've exceeded the maximum amount by ${((locationInventory - quantity) * -1).toLocaleString()} units`,
      });
      return;
    }
    if (quantity == 0) {
      setError('quantity', {
        type: 'validate',
        message: `Quantity must be greater than 0`,
      });
      return;
    }
    transferInventoryMutation.mutate({
      quantity,
      itemId: data.item.id,
      originId: data.fromLocation.id,
      destinationId: data.toLocation.id,
      note: data.note,
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={handleClose}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Transfer inventory</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {errorMessage && (
            <Box sx={{ my: 2 }}>
              <Alert severity="error">{errorMessage}</Alert>
            </Box>
          )}
          <FormBulkItemSearch
            name="item"
            label="Item"
            control={control}
            placeholderText="Select an item"
          />
          <Box>
            <FormLocationSearch
              name="fromLocation"
              label="From location"
              remap={locationInventoryDecorator}
              control={control}
              onCreate={handleCreateLocation}
            />
            {fromLocationQuantity !== null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1.75, mt: 0.5, display: 'block' }}
              >
                {fromLocationQuantity === 'loading'
                  ? 'Loading...'
                  : `${fromLocationQuantity.toLocaleString()} available`}
              </Typography>
            )}
          </Box>

          <Box>
            <FormLocationSearch
              name="toLocation"
              label="To location"
              control={control}
              remap={locationInventoryDecorator}
              onCreate={handleCreateLocation}
            />
            {toLocationQuantity !== null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1.75, mt: 0.5, display: 'block' }}
              >
                {toLocationQuantity === 'loading'
                  ? 'Loading...'
                  : `${toLocationQuantity.toLocaleString()} available`}
              </Typography>
            )}
          </Box>

          <Controller
            name="quantity"
            control={control}
            render={({ field, fieldState }) => {
              return (
                <TextField
                  label="Quantity"
                  type="number"
                  size={'small'}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              );
            }}
          />
          <Controller
            name="note"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Notes"
                multiline
                rows={3}
                placeholder="Optional notes about this transfer"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          size="small"
          type="submit"
          variant="contained"
          color="primary"
          disabled={transferInventoryMutation.isPending}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
